/**
 * A wait she is present for.
 *
 * Twelve places in this app spin, and eight of them used to spin in silence —
 * a grey ring on an empty screen, which is the one thing in the interface that
 * is provably not her. That is the wrong place to be absent: a wait is exactly
 * when somebody is looking at the screen with nothing to do, and it is the
 * cheapest possible moment to be kept company.
 *
 * The mark rather than a ring, because the ring says "the machine is busy" and
 * the mark says "she is fetching it". Same information, and only one of them
 * is anybody.
 *
 * The line is drawn once per wait rather than per render — a live query
 * ticking underneath is not the reader hearing a second joke.
 */

import { cn } from '@gramas/ui'
import { sassLine } from '../sass/engine.js'
import { useSassOnce } from '../sass/useSassOnce.js'
import { GlitimeMark } from './Mark.js'

export interface PersonaSpinnerProps {
  /**
   * Which wait this is, for the catalogue: `scan`, `voice`, `import`, `login`,
   * `photo`, `search`, `foods`, `sync`. A surface with no pool simply gets the
   * mark and no words, which is still better than a bare ring.
   */
  surface: string
  /** Larger for a full-screen wait, smaller inside a sheet. */
  size?: number
  /** Lay the line beside the mark instead of under it. */
  inline?: boolean
  className?: string
}

export function PersonaSpinner({ surface, size = 26, inline, className }: PersonaSpinnerProps) {
  const line = useSassOnce(surface, () => sassLine('loading', { surface }) ?? '')

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2.5 text-mute',
        inline !== true && 'flex-col',
        className,
      )}
      // The words are the announcement; the mark is decoration on top of them.
      role="status"
    >
      <GlitimeMark size={size} alive={size >= 24} station="busy" />
      {line !== '' && <span className="text-sm">{line}</span>}
    </div>
  )
}
